import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IChatBotState } from '../../../interfaces/chatbot/IChatBotState';

export const selectChatbotState =
  createFeatureSelector<IChatBotState>('chatbot');

export const selectChatbotProjectUuid = createSelector(
  selectChatbotState,
  (state) => state.projectUuid,
);

export const selectChatbotConversationsState = createSelector(
  selectChatbotState,
  (state) => state.conversations,
);

export const selectChatbotConversations = createSelector(
  selectChatbotConversationsState,
  (state) => state.items,
);

export const selectChatbotConversationsLoading = createSelector(
  selectChatbotConversationsState,
  (state) => state.loading,
);

export const selectChatbotConversationsLoadingMore = createSelector(
  selectChatbotConversationsState,
  (state) => state.loadingMore,
);

export const selectChatbotConversationsError = createSelector(
  selectChatbotConversationsState,
  (state) => state.error,
);

export const selectChatbotNextCursor = createSelector(
  selectChatbotConversationsState,
  (state) => state.nextCursor,
);

export const selectChatbotCanLoadMore = createSelector(
  selectChatbotNextCursor,
  (cursor) => cursor !== null,
);

export const selectActiveChatUuid = createSelector(
  selectChatbotState,
  (state) => state.activeChatUuid,
);

export const selectActiveConversation = createSelector(
  selectChatbotState,
  (state) => [...state.conversations.items, ...state.archivedConversations.items]
    .find((chat) => chat.chat_uuid === state.activeChatUuid)
    ?? (state.activeChatSummary?.chat_uuid === state.activeChatUuid ? state.activeChatSummary : null),
);

export const selectChatbotArchivedNextCursor = createSelector(
  selectChatbotState, (state) => state.archivedConversations.nextCursor,
);

export const selectChatbotMessages = createSelector(
  selectChatbotState,
  (state) => state.messages,
);

export const selectChatbotLayersState = createSelector(
  selectChatbotState,
  (state) => state.layers,
);

export const selectChatbotLayers = createSelector(
  selectChatbotLayersState,
  (state) => state.items,
);

export const selectChatbotLayerVisibility = createSelector(
  selectChatbotLayersState,
  (state) => state.visibilityByUuid,
);

export const selectChatbotLayerActiveSources = createSelector(
  selectChatbotLayersState,
  (state) => state.activeSourceByUuid,
);

export const selectChatbotLayerRendering = createSelector(
  selectChatbotLayersState,
  (state) => state.renderingByUuid,
);

export const selectChatbotLayerMapErrors = createSelector(
  selectChatbotLayersState,
  (state) => state.mapErrorsByUuid,
);

export const selectChatbotLayerDeleting = createSelector(
  selectChatbotLayersState,
  (state) => state.deletingByUuid,
);

export const selectChatbotLayerDeleteErrors = createSelector(
  selectChatbotLayersState,
  (state) => state.deleteErrorsByUuid,
);

export const selectChatbotLayerByUuid = (layerUuid: string) =>
  createSelector(
    selectChatbotLayers,
    (layers) => layers.find((layer) => layer.layer_uuid === layerUuid) ?? null,
  );

export const selectChatbotLayerIsVisible = (layerUuid: string) =>
  createSelector(
    selectChatbotLayerVisibility,
    (visibility) => visibility[layerUuid] ?? false,
  );

export const selectChatbotLayerIsRendering = (layerUuid: string) =>
  createSelector(
    selectChatbotLayerRendering,
    (rendering) => rendering[layerUuid] ?? false,
  );

export const selectChatbotLayerMapError = (layerUuid: string) =>
  createSelector(
    selectChatbotLayerMapErrors,
    (errors) => errors[layerUuid] ?? null,
  );

export const selectChatbotLayerIsDeleting = (layerUuid: string) =>
  createSelector(
    selectChatbotLayerDeleting,
    (deleting) => deleting[layerUuid] ?? false,
  );

export const selectChatbotLayerDeleteError = (layerUuid: string) =>
  createSelector(
    selectChatbotLayerDeleteErrors,
    (errors) => errors[layerUuid] ?? null,
  );

export const selectChatbotHistoryLoading = createSelector(
  selectChatbotState,
  (state) => state.history.loading,
);

export const selectChatbotHistoryError = createSelector(
  selectChatbotState,
  (state) => state.history.error,
);

export const selectChatbotPromptLoading = createSelector(
  selectChatbotState,
  (state) => state.sendPrompt.loading,
);

export const selectChatbotPromptError = createSelector(
  selectChatbotState,
  (state) => state.sendPrompt.error,
);

/**
 * Presentation model for the chatbot UI.
 *
 * Keep state composition in selectors so the component only renders data and
 * dispatches user intent. Local UI-only state (list/chat page) intentionally
 * stays in the component.
 */
export const selectChatbotViewModel = createSelector(
  selectChatbotState,
  (state) => ({
    conversations: state.conversations.items,
    archivedConversations: state.archivedConversations.items,
    archivedConversationsLoading: state.archivedConversations.loading,
    archivedConversationsLoadingMore: state.archivedConversations.loadingMore,
    archivedConversationsError: state.archivedConversations.error,
    archivedCanLoadMore: state.archivedConversations.nextCursor !== null,
    chatMutations: state.chatMutations,
    chatActionError: state.chatActionError,
    conversationsLoading: state.conversations.loading,
    conversationsLoadingMore: state.conversations.loadingMore,
    conversationsError: state.conversations.error,
    canLoadMore: state.conversations.nextCursor !== null,
    activeChatUuid: state.activeChatUuid,
    activeConversation:
      [...state.conversations.items, ...state.archivedConversations.items].find(
        (conversation) => conversation.chat_uuid === state.activeChatUuid,
      ) ?? (state.activeChatSummary?.chat_uuid === state.activeChatUuid ? state.activeChatSummary : null),
    activeChatArchived: state.activeChatArchived,
    messages: state.messages,
    layers: state.layers.items,
    layerVisibility: state.layers.visibilityByUuid,
    layerActiveSourceUuids: state.layers.activeSourceByUuid,
    layersRendering: state.layers.renderingByUuid,
    layerMapErrors: state.layers.mapErrorsByUuid,
    layersDeleting: state.layers.deletingByUuid,
    layerDeleteErrors: state.layers.deleteErrorsByUuid,
    historyLoading: state.history.loading,
    historyError: state.history.error,
    promptLoading: state.sendPrompt.loading,
    promptError: state.sendPrompt.error,
    canSendPrompt:
      state.projectUuid !== null &&
      !state.history.loading &&
      state.history.error === null &&
      !state.sendPrompt.loading &&
      !state.activeChatArchived &&
      !state.chatMutations[state.activeChatUuid ?? ''],
  }),
);
